import MiniSearch from 'minisearch'
import { getDocumentType } from '../../../utils/search/getDocumentType'

// Re-rank a heterogeneous set of top-nav dropdown candidates (Algolia library
// hits + quick actions + navigation + matched help/external + recent-search
// rows) into a single, comparable order.
//
// Each candidate is projected into a unified document and indexed in ONE
// ad-hoc MiniSearch instance built over the candidate set only. A single
// corpus means a single IDF, so BM25 scores are mutually comparable across
// every row type — which the per-domain full-corpus indexes can't give us.
// This is a re-ranking pass: it never adds recall (we only re-order the rows
// the providers already returned) and it never drops a row — candidates that
// MiniSearch can't score are appended at the end in their original order.

// Shared analyser — mirrors buildMiniSearchIndices.js so behaviour matches the
// content MiniSearch provider (split on non-word chars, lowercase).
function tokenize(str) {
  if (typeof str !== 'string' || str.length === 0) return []
  return str.split(/\W+/).filter((tok) => tok.length > 0)
}

function processTerm(term) {
  if (typeof term !== 'string') return false
  const trimmed = term.trim().toLowerCase()
  return trimmed.length > 0 ? trimmed : false
}

const SEARCH_OPTIONS = {
  prefix: true,
  fuzzy: 0.2,
  combineWith: 'AND',
  boost: {
    name: 3,
    body: 3,
    questionBodies: 2,
    questionChoices: 1,
    choices: 1,
    description: 1,
    label: 2,
    keywordsText: 1,
  },
}

const MINISEARCH_FIELDS = [
  'name', 'body', 'questionBodies', 'questionChoices', 'choices',
  'description', 'label', 'keywordsText',
]

function joinNonEmpty(arrayOfStrings) {
  if (!Array.isArray(arrayOfStrings)) return ''
  return arrayOfStrings.filter((s) => typeof s === 'string' && s.length > 0).join(' ')
}

function findById(list, id) {
  if (!Array.isArray(list) || id == null) return null
  for (let i = 0; i < list.length; i += 1) {
    if (list[i] && list[i].id === id) return list[i]
  }
  return null
}

// Flatten a question's choice bodies into a single string.
function choiceText(question) {
  const choices = (question && Array.isArray(question.choices)) ? question.choices : []
  return joinNonEmpty(choices.map((c) => (c ? c.body : '')))
}

// Flatten every question body + choice body in a set into two strings.
function setQuestionText(set) {
  const questions = (set && Array.isArray(set.questions)) ? set.questions : []
  const bodies = questions.map((q) => (q ? q.body : ''))
  const choices = []
  for (let i = 0; i < questions.length; i += 1) {
    const q = questions[i]
    if (q && Array.isArray(q.choices)) {
      for (let j = 0; j < q.choices.length; j += 1) {
        const c = q.choices[j]
        if (c) choices.push(c.body)
      }
    }
  }
  return { questionBodies: joinNonEmpty(bodies), questionChoices: joinNonEmpty(choices) }
}

// Project a candidate hit into the unified MiniSearch document. The full
// searchable text for sets/questions is pulled from Redux by id (Algolia hits
// don't reliably carry question/choice bodies); the hit's own fields are used
// as a fallback when the Redux record hasn't hydrated.
function buildDoc(candidate, docId, redux) {
  const base = { docId, _hit: candidate }

  if (candidate.__recentSearch) {
    return { ...base, label: candidate.query || '' }
  }
  if (candidate.__quickAction || candidate.__nav) {
    return { ...base, label: candidate.label || '', keywordsText: joinNonEmpty(candidate.keywords) }
  }
  if (candidate.__external) {
    return {
      ...base,
      label: candidate.label || '',
      keywordsText: joinNonEmpty([].concat(candidate.keywords || [], candidate.terms || [])),
    }
  }

  const kind = getDocumentType(candidate)
  const id = candidate.id || candidate.objectID
  if (kind === 'repo') {
    const repo = findById(redux.repos, id)
    return {
      ...base,
      name: (repo && repo.name) || candidate.name || '',
      description: (repo && repo.description) || candidate.description || '',
    }
  }
  if (kind === 'folder') {
    const folder = findById(redux.folders, id)
    return { ...base, name: (folder && folder.name) || candidate.name || '' }
  }
  if (kind === 'set') {
    const set = findById(redux.sets, id) || candidate
    const { questionBodies, questionChoices } = setQuestionText(set)
    return {
      ...base,
      name: (set && set.name) || candidate.name || '',
      questionBodies,
      questionChoices,
    }
  }
  if (kind === 'question') {
    const question = findById(redux.questions, id) || candidate
    return {
      ...base,
      body: (question && question.body) || candidate.body || '',
      choices: choiceText(question),
    }
  }

  // Unknown shape — index whatever label/name it carries so it can still match.
  return { ...base, label: candidate.label || candidate.name || '' }
}

// Stable identity used for de-duplication.
function candidateId(candidate) {
  return (candidate && (candidate.objectID || candidate.id)) || null
}

/**
 * Re-rank dropdown candidates with a single ad-hoc MiniSearch index.
 *
 * @param {Object} input
 * @param {Array<Object>} input.candidates  Heterogeneous hit objects.
 * @param {string} input.query              Current query text.
 * @param {Object} input.redux              { sets, questions, repos, folders }.
 * @returns {Array<Object>}                 Candidates, score-ordered (desc),
 *                                          unscored appended in original order.
 */
export function rescoreCandidates({ candidates, query, redux }) {
  const list = Array.isArray(candidates) ? candidates : []
  const trimmed = (query || '').trim()
  if (list.length === 0 || trimmed.length === 0) return list

  // De-dup by stable id (defensive — providers normally yield unique ids).
  const deduped = []
  const seen = new Set()
  list.forEach((candidate) => {
    const id = candidateId(candidate)
    if (id != null) {
      if (seen.has(id)) return
      seen.add(id)
    }
    deduped.push(candidate)
  })

  const reduxDocs = redux || {}
  const docs = deduped.map((candidate, i) => buildDoc(candidate, `cand-${i}`, reduxDocs))
  const byDocId = new Map(docs.map((doc) => [doc.docId, doc._hit]))

  let results = []
  try {
    const ms = new MiniSearch({
      idField: 'docId',
      fields: MINISEARCH_FIELDS,
      storeFields: ['docId'],
      tokenize,
      processTerm,
    })
    ms.addAll(docs)
    results = ms.search(trimmed, SEARCH_OPTIONS)
  } catch (e) {
    // On any analyser error fall back to the providers' original order.
    return deduped
  }

  const ordered = []
  const placed = new Set()
  results.forEach((result) => {
    const hit = byDocId.get(result.id)
    if (hit && !placed.has(result.id)) {
      placed.add(result.id)
      ordered.push(hit)
    }
  })
  // Item 5: never drop a candidate — append everything MiniSearch didn't score
  // at the end, preserving the providers' original order.
  docs.forEach((doc) => {
    if (!placed.has(doc.docId)) ordered.push(doc._hit)
  })

  return ordered
}

export default rescoreCandidates
